import { apiClient } from '../apiClient'

// ==================== Types ====================

export interface AdminOverview {
  active_users: number
  review_completion: number
  system_uptime: number
  pending_actions: number
  active_campuses: number
  popia_compliance: number
}

export interface Campus {
  id: string
  name: string
  location: string
  active: boolean
  user_count: number
}

export interface SystemRole {
  id: string
  name: string
  permissions: string[]
  user_count: number
}

export interface ObjectiveTemplate {
  id: string
  name: string
  role: string
  objectives: number
  usage: number
}

export interface PerformanceCycle {
  id: number
  name: string
  description?: string | null
  type: 'formal' | 'informal'
  review_type: string
  year: number
  start_date: string
  end_date: string
  /** Calibration closes on this date (null: the check-in end date). HR UAT list #23. */
  calibration_end_date?: string | null
  /** HR's wording for the in-app alert when the cycle opens. */
  launch_message?: string | null
  /** When HR opened the cycle. */
  launched_at?: string | null
  status: 'planning' | 'active' | 'completed' | 'paused'
  requires_rating: boolean
  requires_calibration: boolean
  requires_anonymous_feedback: boolean
  completion_rate: number
  total_participants: number
  completed_reviews: number
}

export interface CycleCreate {
  name: string
  description?: string
  review_type: string
  cycle_type: 'formal' | 'informal'
  year: number
  start_date: string
  end_date: string
  calibration_end_date?: string | null
  launch_message?: string | null
  status?: string
  requires_rating?: boolean
  requires_calibration?: boolean
  requires_anonymous_feedback?: boolean
}

export interface CycleUpdate {
  name?: string
  description?: string
  start_date?: string
  end_date?: string
  calibration_end_date?: string | null
  launch_message?: string | null
  status?: string
  requires_rating?: boolean
  requires_calibration?: boolean
  requires_anonymous_feedback?: boolean
}

export interface CyclePopulationPerson {
  employee_id: string
  name: string
  job_title: string
  function: string
  campus: string
  /** Ring-fenced: the title that left them out (e.g. "tutor (function not recorded)"). */
  reason?: string
  /** Too Soon To Rate: first hire, as a date (YYYY-MM-DD). */
  first_hire?: string | null
}

/** Who a cycle counts (HR UAT list #20-#21). */
export interface CyclePopulation {
  cycle_id: number
  name: string
  review_type: string
  year: number
  checkin_end_date: string | null
  /** Hired after this date: Too Soon To Rate. */
  tstr_line: string | null
  counts: {
    counted: number
    /** Left, though their account is still active (HR UAT list #109). Absent from an older server. */
    no_longer_employed?: number
    out_of_cycle: number
    ring_fenced: number
    too_soon_to_rate: number
  }
  ring_fenced: CyclePopulationPerson[]
  too_soon_to_rate: CyclePopulationPerson[]
}

export interface CycleCompliance {
  cycle_id: string
  evidence_portfolio_completeness: number
  manager_review_completion: number
  anonymous_feedback_submission: number
  idp_integration: number
}

export interface User {
  id: string
  name: string
  email: string
  role: string
  campus: string
  status: 'active' | 'inactive' | 'pending'
  last_login: string
}

export interface UserStats {
  total_users: number
  active_users: number
  pending_setup: number
  inactive_users: number
  new_this_month: number
}

export interface PerformanceDistribution {
  overall: {
    exceptional: { count: number; percentage: number }
    full: { count: number; percentage: number }
    not_yet_full: { count: number; percentage: number }
    weak: { count: number; percentage: number }
  }
  by_department: Record<string, any>
  by_campus: Record<string, any>
}

export interface TalentPipeline {
  nine_box_grid: Record<string, { count: number; percentage: number }>
  total_employees: number
}

export interface ProcessEfficiency {
  review_cycle_completion: { value: number; target: number; status: string }
  evidence_quality_score: { value: number; target: number; status: string }
  manager_effectiveness: { value: number; target: number; status: string }
  idp_completion_rate: { value: number; target: number; status: string }
}

export interface ComplianceOverview {
  popia_compliance: number
  active_audits: number
  data_requests: number
  incidents: number
  consent_tracking: { total: number; synced: number; errors: number }
  data_retention: { status: string; auto_archival: boolean }
  access_control: { status: string; rbac_enabled: boolean }
  data_encryption: { status: string; encryption: string }
}

export interface DataRequest {
  id: string
  type: string
  requester: string
  submitted: string
  status: 'pending' | 'in-progress' | 'completed'
  due_date: string
}

export interface IntegrationStatus {
  f_and_o: {
    status: string
    last_sync: string
    records_synced: number
    sync_complete: boolean
    entities: Record<string, { total: number; synced: number; errors: number }>
  }
  sage: {
    status: string
    last_sync: string
    data_integrity: number
  }
  performance_metrics: {
    avg_response_time_ms: number
    success_rate: number
    sync_frequency_hours: number
    error_rate: number
  }
}

export interface SyncHistory {
  source: string
  time: string
  records: number
  status: 'success' | 'failed'
}

/** A review cycle the Bonus report can take its performance rating from. */
export interface BonusReportCycle {
  review_type: 'midyear' | 'yearend'
  year: number
  label: string          // "2026 Mid-year"
  completed: number      // people with a completed review
  calibrated: number     // people with a calibrated rating
}

// KPA Framework Types
export interface KPAFrameworkItem {
  id: string
  name: string
  display_name: string
  description: string | null
  weight_percentage: number | null
  sort_order: number
  functional_area_ids: string[] | null
  created_at: string | null
  updated_at: string | null
}

export interface KPAFrameworkPillar {
  pillar_id: string
  pillar_name: string
  pillar_description: string | null
  kpas: KPAFrameworkItem[]
  kpa_count: number
}

export interface KPAFramework {
  framework: KPAFrameworkPillar[]
  total_pillars: number
  total_kpas: number  // Unique KPA count
  total_kpa_pillar_links?: number  // Total KPA appearances across pillars (can be > total_kpas)
}

export interface KPAFrameworkStats {
  total_kpas: number
  linked_kpis: number
  linked_objectives: number
  avg_kpis_per_kpa: number
  avg_objectives_per_kpa: number
}

export interface KPACreate {
  name: string
  display_name?: string | null
  description?: string | null
  strategic_pillar_id: string
  sort_order?: number | null
  weight_percentage?: number | null
}

export interface KPAUpdate {
  name?: string
  display_name?: string | null
  description?: string | null
  strategic_pillar_id?: string
  sort_order?: number | null
  weight_percentage?: number | null
}

// ==================== Usage Analytics Types ====================

export interface UsageSummary {
  total_users: number
  active_7d: number
  active_30d: number
  dau: number
  wau: number
  mau: number
  new_users_month: number
  adoption_rate: number
}

export interface LoginActivityDay {
  date: string
  login_count: number
  unique_users: number
}

export interface LoginActivity {
  days: number
  data: LoginActivityDay[]
}

export interface FeatureAdoptionItem {
  name: string
  count: number
  total?: number
  icon: string
}

export interface FeatureAdoption {
  period: string
  features: FeatureAdoptionItem[]
}

export interface CampusBreakdownItem {
  campus: string
  total_users: number
  active_users: number
  adoption_rate: number
}

export interface CampusBreakdown {
  campuses: CampusBreakdownItem[]
}

export interface FunctionalAreaBreakdownItem {
  functional_area: string
  total_users: number
  active_users: number
  adoption_rate: number
}

export interface FunctionalAreaBreakdown {
  functional_areas: FunctionalAreaBreakdownItem[]
}

export interface AdoptionFunnelStage {
  stage: string
  count: number
}

export interface AdoptionFunnel {
  funnel: AdoptionFunnelStage[]
}

export interface OnboardingStats {
  stage_counts: Record<string, number>
  total_users: number
  users_with_onboarding: number
  completion_rate: number
  avg_duration_minutes: number
  median_duration_minutes: number
  scenario_breakdown: Record<string, number>
}

export interface ProfileCompletionStats {
  total_users: number
  qualifications_completed: number
  work_history_completed: number
  expertise_completed: number
  all_sections_completed: number
  completion_distribution: Record<string, number>
  avg_completion_percentage: number
}

// ==================== API Service ====================

class AdminService {
  private baseURL = '/api/v1/admin'

  // ==================== System Overview ====================
  
  async getOverview(): Promise<AdminOverview> {
    const response = await apiClient.get<AdminOverview>(`${this.baseURL}/overview`)
    return response.data
  }

  // ==================== System Configuration ====================
  
  async getCampuses(): Promise<{ campuses: Campus[]; total: number }> {
    const response = await apiClient.get<{ campuses: Campus[]; total: number }>(`${this.baseURL}/campuses`)
    return response.data
  }

  async getSystemRoles(): Promise<{ roles: SystemRole[]; total: number }> {
    const response = await apiClient.get<{ roles: SystemRole[]; total: number }>(`${this.baseURL}/roles`)
    return response.data
  }

  async getObjectiveTemplates(roleType?: string): Promise<{ templates: ObjectiveTemplate[]; total: number }> {
    const params = roleType ? { role_type: roleType } : {}
    const response = await apiClient.get<{ templates: ObjectiveTemplate[]; total: number }>(`${this.baseURL}/templates`, { params })
    return response.data
  }

  // ==================== KPA Framework Management ====================
  
  async getKPAFramework(strategicPillarId?: number): Promise<KPAFramework> {
    const params = strategicPillarId ? { strategic_pillar_id: strategicPillarId } : {}
    const response = await apiClient.get<KPAFramework>(`${this.baseURL}/kpa-framework`, { params })
    return response.data
  }

  async getKPAFrameworkStats(): Promise<KPAFrameworkStats> {
    const response = await apiClient.get<KPAFrameworkStats>(`${this.baseURL}/kpa-framework/stats`)
    return response.data
  }

  async createKPA(kpaData: KPACreate): Promise<KPAFrameworkItem> {
    const response = await apiClient.post<KPAFrameworkItem>(`${this.baseURL}/kpa-framework`, kpaData)
    return response.data
  }

  async updateKPA(kpaId: string, kpaData: KPAUpdate): Promise<KPAFrameworkItem> {
    const response = await apiClient.put<KPAFrameworkItem>(`${this.baseURL}/kpa-framework/${kpaId}`, kpaData)
    return response.data
  }

  async deleteKPA(kpaId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`${this.baseURL}/kpa-framework/${kpaId}`)
    return response.data
  }

  // ==================== Performance Cycle Administration ====================
  
  async getPerformanceCycles(params?: { status?: string; year?: number }): Promise<{ cycles: PerformanceCycle[]; total: number }> {
    const response = await apiClient.get<{ cycles: PerformanceCycle[]; total: number }>(`${this.baseURL}/cycles`, { params })
    return response.data
  }

  async createCycle(data: CycleCreate): Promise<PerformanceCycle> {
    const response = await apiClient.post<PerformanceCycle>(`${this.baseURL}/cycles`, data)
    return response.data
  }

  async updateCycle(cycleId: number, data: CycleUpdate): Promise<PerformanceCycle> {
    const response = await apiClient.put<PerformanceCycle>(`${this.baseURL}/cycles/${cycleId}`, data)
    return response.data
  }

  async deleteCycle(cycleId: number): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`${this.baseURL}/cycles/${cycleId}`)
    return response.data
  }

  /** Who a cycle counts, for HR to sign off before launch (HR UAT list #20-#21). */
  async getCyclePopulation(cycleId: number): Promise<CyclePopulation> {
    const response = await apiClient.get<CyclePopulation>(`${this.baseURL}/cycles/${cycleId}/population`)
    return response.data
  }

  async getCycleCompliance(cycleId: number): Promise<CycleCompliance> {
    const response = await apiClient.get<CycleCompliance>(`${this.baseURL}/cycles/${cycleId}/compliance`)
    return response.data
  }

  // ==================== User Management ====================
  
  async getUsers(params?: {
    role?: string
    campus?: string
    status?: string
    search?: string
    skip?: number
    limit?: number
  }): Promise<{ users: User[]; total: number; skip: number; limit: number }> {
    const response = await apiClient.get<{ users: User[]; total: number; skip: number; limit: number }>(`${this.baseURL}/users`, { params })
    return response.data
  }

  async getUserStats(): Promise<UserStats> {
    const response = await apiClient.get<UserStats>(`${this.baseURL}/users/stats`)
    return response.data
  }

  // ==================== Analytics & Reporting ====================
  
  async getPerformanceDistribution(params?: {
    campus?: string
    department?: string
  }): Promise<PerformanceDistribution> {
    const response = await apiClient.get<PerformanceDistribution>(
      `${this.baseURL}/analytics/performance-distribution`,
      { params }
    )
    return response.data
  }

  async getTalentPipeline(): Promise<TalentPipeline> {
    const response = await apiClient.get<TalentPipeline>(`${this.baseURL}/analytics/talent-pipeline`)
    return response.data
  }

  async getProcessEfficiency(): Promise<ProcessEfficiency> {
    const response = await apiClient.get<ProcessEfficiency>(`${this.baseURL}/analytics/process-efficiency`)
    return response.data
  }

  // ==================== Compliance & Governance ====================
  
  async getComplianceOverview(): Promise<ComplianceOverview> {
    const response = await apiClient.get<ComplianceOverview>(`${this.baseURL}/compliance/overview`)
    return response.data
  }

  async getDataRequests(status?: string): Promise<{ requests: DataRequest[]; total: number }> {
    const params = status ? { status } : {}
    const response = await apiClient.get<{ requests: DataRequest[]; total: number }>(`${this.baseURL}/compliance/data-requests`, { params })
    return response.data
  }

  // ==================== Integration Management ====================
  
  async getIntegrationStatus(): Promise<IntegrationStatus> {
    const response = await apiClient.get<IntegrationStatus>(`${this.baseURL}/integrations/status`)
    return response.data
  }

  async getSyncHistory(limit: number = 10): Promise<{ history: SyncHistory[]; total: number }> {
    const response = await apiClient.get<{ history: SyncHistory[]; total: number }>(`${this.baseURL}/integrations/sync-history`, {
      params: { limit }
    })
    return response.data
  }

  // ==================== Usage & Adoption Analytics ====================

  async getUsageSummary(): Promise<UsageSummary> {
    const response = await apiClient.get<UsageSummary>(`${this.baseURL}/usage-analytics/summary`)
    return response.data
  }

  async getLoginActivity(days: number = 30): Promise<LoginActivity> {
    const response = await apiClient.get<LoginActivity>(`${this.baseURL}/usage-analytics/login-activity`, {
      params: { days }
    })
    return response.data
  }

  async getFeatureAdoption(): Promise<FeatureAdoption> {
    const response = await apiClient.get<FeatureAdoption>(`${this.baseURL}/usage-analytics/feature-adoption`)
    return response.data
  }

  async getCampusBreakdown(): Promise<CampusBreakdown> {
    const response = await apiClient.get<CampusBreakdown>(`${this.baseURL}/usage-analytics/campus-breakdown`)
    return response.data
  }

  async getFunctionalAreaBreakdown(): Promise<FunctionalAreaBreakdown> {
    const response = await apiClient.get<FunctionalAreaBreakdown>(`${this.baseURL}/usage-analytics/functional-area-breakdown`)
    return response.data
  }

  async getAdoptionFunnel(): Promise<AdoptionFunnel> {
    const response = await apiClient.get<AdoptionFunnel>(`${this.baseURL}/usage-analytics/adoption-funnel`)
    return response.data
  }

  async getOnboardingStats(): Promise<OnboardingStats> {
    const response = await apiClient.get<OnboardingStats>(`${this.baseURL}/usage-analytics/onboarding-stats`)
    return response.data
  }

  async getProfileCompletionStats(): Promise<ProfileCompletionStats> {
    const response = await apiClient.get<ProfileCompletionStats>(`${this.baseURL}/usage-analytics/profile-completion-stats`)
    return response.data
  }

  /** Also reads F&O for the qualification check, so it can take a few minutes. */
  async downloadProfessionalDetailsExport(): Promise<void> {
    const response = await apiClient.get(
      `${this.baseURL}/usage-analytics/professional-details-export`,
      { responseType: 'blob', timeout: 300000 }
    )

    const contentDisposition = response.headers['content-disposition'] || ''
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch ? filenameMatch[1] : professionalDetailsFilename(new Date())

    const url = URL.createObjectURL(response.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async downloadLineManagerExport(): Promise<void> {
    const response = await apiClient.get(
      `${this.baseURL}/usage-analytics/line-manager-export`,
      { responseType: 'blob', timeout: 120000 }
    )

    const contentDisposition = response.headers['content-disposition'] || ''
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch ? filenameMatch[1] : 'Line_Manager_Export.xlsx'

    const url = URL.createObjectURL(response.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async downloadOnboardingObjectivesExport(): Promise<void> {
    const response = await apiClient.get(
      `${this.baseURL}/usage-analytics/onboarding-objectives-export`,
      { responseType: 'blob', timeout: 120000 }
    )

    const contentDisposition = response.headers['content-disposition'] || ''
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch ? filenameMatch[1] : 'Onboarding_Objectives_Export.xlsx'

    const url = URL.createObjectURL(response.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async downloadPositionGradeExport(): Promise<void> {
    const response = await apiClient.get(
      `${this.baseURL}/usage-analytics/position-grade-export`,
      { responseType: 'blob', timeout: 120000 }
    )

    const contentDisposition = response.headers['content-disposition'] || ''
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch ? filenameMatch[1] : 'Position_Grade_Export.xlsx'

    const url = URL.createObjectURL(response.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async downloadHeadcountExport(): Promise<void> {
    const response = await apiClient.get(
      `${this.baseURL}/usage-analytics/headcount-export`,
      { responseType: 'blob', timeout: 300000 }
    )

    const contentDisposition = response.headers['content-disposition'] || ''
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch ? filenameMatch[1] : 'Headcount_Export.xlsx'

    const url = URL.createObjectURL(response.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /** Check-in & calibration statistics for one period, over the people it counts. */
  async downloadCheckInCalibrationExport(reviewType: 'midyear' | 'yearend' = 'midyear'): Promise<void> {
    const response = await apiClient.get(
      `${this.baseURL}/usage-analytics/checkin-calibration-export`,
      { responseType: 'blob', timeout: 300000, params: { review_type: reviewType } }
    )

    const contentDisposition = response.headers['content-disposition'] || ''
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch ? filenameMatch[1] : 'Check-in_Calibration_Statistics.xlsx'

    const url = URL.createObjectURL(response.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /** Review cycles holding ratings, newest first — the Bonus report's choices. */
  async getBonusReportCycles(): Promise<BonusReportCycle[]> {
    const response = await apiClient.get<{ cycles: BonusReportCycle[] }>(
      `${this.baseURL}/usage-analytics/bonus-report/cycles`
    )
    return response.data.cycles
  }

  /** The Bonus report, rated from one review cycle. Reads Sage and F&O live, so
   *  it can take a few minutes. */
  async downloadBonusReport(cycle: Pick<BonusReportCycle, 'review_type' | 'year'>): Promise<void> {
    const response = await apiClient.get(
      `${this.baseURL}/usage-analytics/bonus-report-export`,
      {
        params: { review_type: cycle.review_type, year: cycle.year },
        responseType: 'blob',
        timeout: 600000,
      }
    )

    // The gateway exposes no Content-Disposition to the browser, so the server's
    // name (cycle and time) never arrives; compose the same one here rather than
    // fall back to a bare "Eduvos_Bonus_Report.xlsx" (UAT, 2 Oct 2026).
    const contentDisposition = response.headers['content-disposition'] || ''
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch ? filenameMatch[1] : bonusReportFilename(cycle, new Date())

    const url = URL.createObjectURL(response.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
}

/** YYYYMMDD_HHMM in South African time, as the exports stamp their file names
 *  and their own "Generated" lines. */
function sastStamp(at: Date): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(at).map((p) => [p.type, p.value]),
  )
  return `${parts.year}${parts.month}${parts.day}_${parts.hour}${parts.minute}`
}

/** The Bonus report's file name, as performance-service names it:
 *  Eduvos_Bonus_Report_2026_Mid-year_20261002_0923.xlsx. */
export function bonusReportFilename(
  cycle: Pick<BonusReportCycle, 'review_type' | 'year'>,
  at: Date,
): string {
  const half = cycle.review_type === 'midyear' ? 'Mid-year' : 'Year-end'
  return `Eduvos_Bonus_Report_${cycle.year}_${half}_${sastStamp(at)}.xlsx`
}

/** The Professional Details export's file name, as performance-service names it:
 *  Professional_Details_Export_20261007_1544.xlsx (downloads had been arriving as
 *  "Professional_Details_Export (13).xlsx": the gateway hides the server's name). */
export function professionalDetailsFilename(at: Date): string {
  return `Professional_Details_Export_${sastStamp(at)}.xlsx`
}

export const adminService = new AdminService()
export default adminService
